import { Add, ArrowBack, Close } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { useNotification } from '../../../hooks/NotificationContext';
import { isExecutiveManager } from '../../../lib/userUtils';
import {
  addTestCasesToSuite,
  getTestCasesBySuite,
  removeTestCasesfromSuite,
} from '../../../modules/testsuites';
import { getConfigurations } from '../../../modules/config';
import ConfirmationModal from '../../elements/ConfirmationModal';
import Modal from '../../elements/Modal';
import TableComponent from '../../elements/TableComponent';
import { tableHeader } from '../test-case/testCaseTableHead';
import AddTestCaseInSuite from './AddTestCaseInSuite';

const ViewTestCaseModal = ({
  show,
  handleClose,
  selectedSuite,
  fetchTestSuitesData,
}) => {
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState('view');
  const { projectId } = useParams();
  const { addNotification } = useNotification();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [removeTestCase, setRemoveTestCase] = useState(null);
  const [technologyLayerOptions, setTechnologyLayerOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const isSuiteSuspended = selectedSuite?.status === 'suspended';

  const fetchTestCases = async () => {
    if (!selectedSuite?.id) return;

    setLoading(true);
    try {
      const response = await getTestCasesBySuite(selectedSuite?.id,
        page,
        perPage,
      );
      setData(response.testcases);
      setTotalCount(response.total);
      setError(null);
    } catch (error) {
      setError(error.message || 'Unable to fetch test cases');
    } finally {
      setLoading(false);
    }
  };
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  const fetchConfig = async () => {
  try {
    const [technologyLayerResponse] = await Promise.all([
      getConfigurations('technologylayer', projectId), 
    ]);
    if (technologyLayerResponse) {
      setTechnologyLayerOptions(technologyLayerResponse.data || []);
    }
  } catch (error) {
    console.log(error);
  }
};


  useEffect(() => {
    if (show) {
      fetchTestCases();
      fetchConfig();
    }
  }, [show, page, perPage, order]);

  const handleCloseButton = () => {
    setSelected([]);
    setMode('view');
    setPage(1);
    setTotalCount(0);
    handleClose();
    setData([]);
  };

  const handleToggleModes = (mode) => {
    setMode(mode);
  };

  const handleAddTestCases = async () => {
    setLoading(true);
    const testCaseIds = selected;
    try {
      await addTestCasesToSuite(selectedSuite?.id, testCaseIds);
      addNotification('success', 'Test cases added successfully.');
      fetchTestSuitesData();
      handleCloseButton();
    } catch (error) {
      const errorMessage = error?.message || 'Failed to fetch test cases.';
      addNotification('danger', errorMessage);
    }
    setLoading(false);
  };

  const handleTestCaseRemove = async () => {
    try {
      await removeTestCasesfromSuite(selectedSuite?.id, removeTestCase?.id);
      fetchTestCases();
      fetchTestSuitesData();
      addNotification('success', 'Test case has been removed from test suite.');
    } catch (error) {
      const errorMessage = error?.message || 'Failed to remove test case.';
      addNotification('danger', errorMessage);
    }
    setShowConfirmationModal(false);
  };

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
  };
  return (
    <>
      <Modal
        modalTitle={
          <>
            Manage Test Cases -{' '}
            <span
              style={{
                fontWeight: 'bold',
                color: 'var(--primary-color)',
              }}>
              {selectedSuite?.name || 'Unnamed Suite'}
              <span
                style={{ fontSize: "10px", color: "red", marginLeft: "5px" }}
              >
                {isSuiteSuspended &&
                  "(Suspended)"}
              </span>
            </span>
          </>
        }
        open={show}
        handleClose={handleCloseButton}
        maxWidth="lg"
        action={() => (
          <>
            <Button
              title="Close"
              variant="outlined"
              color="secondary"
              onClick={handleCloseButton}>
              Close
            </Button>
            {mode === 'add' && (
              <Button
                title="Add select Test Cases"
                variant="contained"
                disabled={selected.length === 0}
                onClick={handleAddTestCases}>
                Add
              </Button>
            )}
          </>
        )}>
        <Container fluid className="mt-2">
          {error && <p className="text-danger mt-2 text-center">{error}</p>}

          {mode === 'view' ? (
            <>
              {!isExecutiveManager() && !isSuiteSuspended && <Button
                variant="contained"
                color="primary"
                onClick={() => handleToggleModes('add')}>
                <Add /> Add Test Cases
              </Button>}
              <TableComponent
                data={data}
                headerTabs={tableHeader}
                order={order}
                setOrder={setOrder}
                defaultSort="created_at"
                isLoading={loading}
                striped
                page={page}
                perPage={perPage}
                totalCount={totalCount}
                showPages={true}
                showCount={true}
                handlePageChange={handlePageChange}
                setPerPage={handlePageSizeChange}
                showRowCount={true}
                action={(row) => {
                  return (
                    !isExecutiveManager() && !isSuiteSuspended &&
                    <IconButton
                      onClick={() => {
                        setShowConfirmationModal(true);
                        setRemoveTestCase(row);
                      }}
                      title="Remove Test Case">
                      <Close fontSize="small" sx={{ color: 'red' }} />
                    </IconButton>
                  );
                }}
              />
            </>
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleToggleModes('view')}>
                <ArrowBack fontSize="small" /> View added Test Cases
              </Button>
              <AddTestCaseInSuite
                selectedSuite={selectedSuite}
                selected={selected}
                setSelected={setSelected}
                technologyLayerOptions={technologyLayerOptions}
              />
            </>
          )}
        </Container>
      </Modal>
      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        handleConfirm={handleTestCaseRemove}
        title="Remove Test Case"
        body={`Are you sure you want to remove test case "${removeTestCase?.key}" from this test suite?`}
        buttonLabel="Remove"
        buttonLabelSecondary="Cancel"
        buttonStyle="primary"
      />
    </>
  );
};

export default ViewTestCaseModal;